import { WorldMapMarkerDetailStyle } from '__generated__/globalTypes';
import { DetailListModal } from 'components/site/modals/DetailListModal';
import { DetailListModalMini } from 'components/site/modals/DetailListModalMini';
import { DetailModal } from 'components/site/modals/DetailModal';
import { DetailModalMini } from 'components/site/modals/DetailModalMini';
import { FilterModal } from 'components/site/modals/FilterModal';
import { StoryListModal } from 'components/site/modals/StoryListModal';
import { StoryListModalMini } from 'components/site/modals/StoryListModalMini';
import { Acc2Gradient } from 'elements/visual/Acc2Gradient';
import { GlobalUser } from 'init/GlobalUser';
import { EFonts } from 'libs/styles/styling';
import { useSetLanguage } from 'libs/utils/site/loadLanguage';
import { useContext, useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { CtxApp } from 'session/ctxApp';
import { CtxDetail } from 'session/ctxDetail';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal, ModalStateContent } from 'session/ctxModal';
import { CtxView } from 'session/ctxView';
import { GlobalContext } from 'session/state/context';
import { MapheimRouter } from 'static/routes/Router';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const AppContent = (props: Props): JSX.Element => {
    const ctxModal = useContext(CtxModal);
    const ctxView = useContext(CtxView);
    const ctxEdit = useContext(CtxEdit);
    const ctxDetail = useContext(CtxDetail);
    const ctxApp = useContext(CtxApp);
    const { state } = useContext(GlobalContext);
    useSetLanguage();

    async function debug() {
        console.log('%c Debug', 'background-color: #29cc29');
        console.log('ctxModal: ', ctxModal);
        console.log('ctxView: ', ctxView);
        console.log('ctxEdit: ', ctxEdit);
        console.log('ctxDetail: ', ctxDetail);

        // ctxModal.setDetailModal({ id: '9a7348de-7c2b-4463-846c-46f17ca828b8' });
        // const query = await detailCRUD.readDetail(
        //     '9a7348de-7c2b-4463-846c-46f17ca828b8',
        // );
        // const detail = query.data.moment;
        // ctxModal.setDetailModal({ id: detail.storyId, detail: detail });
    }

    useEffect(() => {
        if (window.location.pathname === '/rollup') {
            window.location.replace('https://mapheim.com');
        }
    }, []);

    const map = ctxView.map || ctxEdit.map;

    const isMini = ctxView.customPageConfig?.markerDetailStyle
        ? ctxView.customPageConfig?.markerDetailStyle === 'Mini'
        : map?.markerDetailStyle === WorldMapMarkerDetailStyle.MINI;

    const isBubble = ctxView.customPageConfig?.markerDetailStyle
        ? ctxView.customPageConfig?.markerDetailStyle === 'Bubble'
        : map?.markerDetailStyle === WorldMapMarkerDetailStyle.BUBBLE;

    const isMapV2 = ctxView.customPageConfig?.markerDetailStyle
        ? ctxView.customPageConfig?.markerDetailStyle === 'MapV2'
        : map?.markerDetailStyle === WorldMapMarkerDetailStyle.MAP_V2;

    const getDetailComponent = () => {
        // Bubble is handled separately
        if (isBubble) {
            return null;
        }
        // MapV2 Has one global modal
        if (isMapV2) {
            return null;
        }
        if (isMini) {
            return <DetailModalMini />;
        }
        return <DetailModal />;
    };

    const getDetailListComponent = () => {
        // Bubble is handled separately
        if (isBubble) {
            return null;
        }
        // MapV2 Has one global modal
        if (isMapV2) {
            return null;
        }
        if (isMini) {
            return <DetailListModalMini storyIds={ctxModal.detailListModal.storyIds} />;
        }
        return <DetailListModal storyIds={ctxModal.detailListModal.storyIds} />;
    };

    const getStoryListComponent = () => {
        if (isMini) {
            return <StoryListModalMini />;
        }
        // MapV2 Has one global modal
        if (isMapV2) {
            return null;
        }
        return <StoryListModal />;
    };

    function hideCrashMessage() {
        if (window.location?.pathname?.includes('/m/')) {
            return true;
        }
        return false;
    }

    const RoutesMapheim = useMemo(() => {
        return (
            <>
                <BrowserRouter>
                    <MapheimRouter />
                    {ctxModal.showListOfStories && getStoryListComponent()}
                    {ctxModal.detailListModal && getDetailListComponent()}
                    {ctxModal.showFilters && <FilterModal />}
                    {ctxModal.detailModal?.id && getDetailComponent()}
                </BrowserRouter>
                <Acc2Gradient />
                <GlobalUser />
            </>
        );
    }, [
        isMapV2,
        ctxModal.showListOfStories,
        ctxModal.detailListModal,
        ctxModal.showFilters,
        ctxModal.detailModal?.id
    ]);

    if (state.dbLikelyCrashed && !hideCrashMessage()) {
        return (
            <S_Page>
                <S_Block>
                    <S_Title>The site is currently unavailable</S_Title>
                    <S_Desc>
                        We are sorry but the site is under construction. Please try it again later
                    </S_Desc>
                </S_Block>
            </S_Page>
        );
    }

    return (
        <S_AppContent className={props.className} key={ctxApp.language ?? 'noLanguage'}>
            <ModalStateContent />

            {/* {ctxSX.flow && <SXFlow />} */}
            {RoutesMapheim}
            {(window.location.href.includes('localhost') ||
                window.location.href.includes('ngrok')) && <S_DevTools onClick={debug} />}
        </S_AppContent>
    );
};

const S_AppContent = styled.div`
    display: flex;
    width: 100%;
    position: relative;
`;

const S_DevTools = styled.div`
    position: absolute;
    width: 20px;
    height: 20px;
    bottom: 0px;
    left: 0px;
    background-color: red;
    cursor: pointer;
    z-index: 9999999999;
`;

const S_Page = styled.div`
    width: 100vw;
    height: 100vh;
    background-color: rgb(246, 249, 255);
    position: fixed;
    top: 0px;
    left: 0px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 15px;
`;

const S_Block = styled.div`
    border-radius: 45px;
    background-color: rgb(234, 240, 245);
    padding: 37px 42px;
`;

const S_Title = styled.div`
    font-size: 25px;
    margin: 10px 0px 18px;
    color: rgb(4, 19, 33);
    font-family: 'InterBold';
    letter-spacing: -0.756px;
    line-height: 37px;
`;

const S_Desc = styled.div`
    color: rgb(74, 74, 74);
    font-family: ${EFonts.InterRegular};
    font-size: 18px;
    line-height: 37px;
`;
